import { useMemo, useState, useEffect, useCallback } from "react";
import {
  Card,
  IndexTable,
  BlockStack,
  SkeletonBodyText,
  IndexFilters,
  ChoiceList,
  Page,
  IndexFiltersMode,
  Text,
  Box,
  Toast,
  InlineStack,
  Link,
  Icon,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import type { Payment, Event } from "@features/events/types";
import { IndexTableHeading } from "@medentic/polaris-react/build/ts/src/components/IndexTable/IndexTable";
import { NonEmptyArray } from "@medentic/polaris-react/build/ts/src/types";
import { AppPageWrapper } from "@shared/ui";
import { RenderDate } from "@shared/ui";
import { TabsTableSkeleton } from "@shared/ui";
import AssignTicketsModal from "./AssignTicketsModal";
import PaymentAttendeesCell from "./PaymentAttendeesCell";
import { ExternalSmallIcon } from "@medentic/polaris-icons";
import EventBadgeRenderer from "@features/events/components/EventBadgeRenderer";
import { Currency } from "@shared/ui";

export default function EventPayments() {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { GET } = useApiRequest();

  // Fetch all events
  const { data: allEvents = [] } = useQuery<Event[]>({
    queryKey: ["all-events", hcc],
    queryFn: () => GET(`/api/healthcare-companies/${hcc}/e/events`),
    enabled: Boolean(hcc),
  });

  // Only mine events
  const myEvents = useMemo(
    () => allEvents.filter((e: Event) => e.isMy),
    [allEvents]
  );

  // Tabs for payout status
  const tabs = [
    { content: t("eventTransactions.all"), index: 0, id: "all" },
    { content: t("eventTransactions.pending"), index: 1, id: "pending" },
    { content: t("eventTransactions.paid"), index: 2, id: "paid" },
  ];

  // Initialize state from URL params
  const [selectedTab, setSelectedTab] = useState(() => {
    const status = searchParams.get("status");
    if (status === "pending") return 1;
    if (status === "paid") return 2;
    return 0;
  });
  const [selectedEventId, setSelectedEventId] = useState(
    () => searchParams.get("eventId") || ""
  );
  const [queryValue, setQueryValue] = useState(
    () => searchParams.get("search") || ""
  );
  const [debouncedQuery, setDebouncedQuery] = useState(
    () => searchParams.get("search") || ""
  );
  const [mode, setMode] = useState(IndexFiltersMode.Default);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignPayment, setAssignPayment] = useState<Payment | null>(null);
  const [toast, setToast] = useState<{
    content: string;
    error?: boolean;
  } | null>(null);
  const queryClient = useQueryClient();

  // Debounce the search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(queryValue);
    }, 500);

    return () => clearTimeout(timer);
  }, [queryValue]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedTab === 1) params.set("status", "pending");
    if (selectedTab === 2) params.set("status", "paid");
    if (selectedEventId) params.set("eventId", selectedEventId);
    if (debouncedQuery) params.set("search", debouncedQuery);
    setSearchParams(params);
  }, [selectedTab, selectedEventId, debouncedQuery, setSearchParams]);

  const payoutStatus =
    selectedTab === 1 ? "pending" : selectedTab === 2 ? "paid" : "";

  // Fetch payments
  const {
    data: payments = [],
    isLoading,
    isFetching,
  } = useQuery<Payment[]>({
    queryKey: [
      "event-payments",
      hcc,
      payoutStatus,
      selectedEventId,
      debouncedQuery,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (payoutStatus) params.append("payoutStatus", payoutStatus);
      if (selectedEventId) params.append("eventId", selectedEventId);
      if (debouncedQuery) params.append("query", debouncedQuery);
      return GET(`/api/healthcare-companies/${hcc}/e/finance/transactions?${params}`);
    },
    enabled: Boolean(hcc),
    placeholderData: keepPreviousData,
  });

  // Table headings
  const headings = [
    { title: t("eventTransactions.date") },
    { title: t("eventTransactions.name") },
    { title: t("eventTransactions.event") },
    { title: t("eventTransactions.ticket") },
    { title: t("eventTransactions.attendees"), alignment: "end" },
    { title: t("eventTransactions.quantity"), alignment: "end" },
    { title: t("eventTransactions.amount"), alignment: "end" },
    { title: t("eventTransactions.fees"), alignment: "end" },
    { title: t("eventTransactions.paymentMethod") },
    { title: t("eventTransactions.payoutStatus") },
    { title: t("eventTransactions.invoice") },
  ];

  const handleAssignClick = useCallback((payment: Payment) => {
    setAssignPayment(payment);
    setAssignModalOpen(true);
  }, []);

  const handleAssignSuccess = useCallback(
    (multi: boolean, names?: string[]) => {
      setToast({
        content: multi
          ? t("eventTransactions.ticketsAssignedSuccess")
          : t("eventTransactions.ticketAssignedTo", {
              name: names?.[0] || t("eventTransactions.attendee"),
            }),
      });
      setAssignModalOpen(false);
      setAssignPayment(null);
      queryClient.invalidateQueries({ queryKey: ["event-payments", hcc] });
    },
    [hcc, queryClient, t]
  );

  const handleAssignClose = useCallback(() => {
    setAssignModalOpen(false);
    setAssignPayment(null);
  }, []);

  // Table rows
  const rowMarkup = payments.map((p, idx) => (
    <IndexTable.Row id={p.id} key={p.id} position={idx}>
      <IndexTable.Cell>
        <RenderDate date={p.createdAt} isAgoFormat={true} reverseTooltip />
      </IndexTable.Cell>
      <IndexTable.Cell>{p.payerName}</IndexTable.Cell>
      <IndexTable.Cell>
        <Box maxWidth="220px">
          <Text as="span" variant="bodySm" truncate>
            {p.eventTitle}
          </Text>
        </Box>
      </IndexTable.Cell>
      <IndexTable.Cell>
        {p.assignedTickets && p.assignedTickets.length > 0
          ? [...new Set(p.assignedTickets.map((t) => t.title))].join(", ")
          : "-"}
      </IndexTable.Cell>
      <IndexTable.Cell>
        <PaymentAttendeesCell payment={p} onAssignClick={handleAssignClick} />
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" variant="bodySm" alignment="end">
          {p.quantity}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" variant="bodySm" alignment="end">
          <Currency value={p.amount} currency={p.currency} />
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" variant="bodySm" alignment="end">
          <Currency value={p.fees} currency={p.currency} />
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>{p.paymentMethod}</IndexTable.Cell>
      <IndexTable.Cell>
        <EventBadgeRenderer slug={p.payoutStatus} entity="paymentPayout" />
      </IndexTable.Cell>
      <IndexTable.Cell>
        {p.invoiceLink ? (
          <Link url={p.invoiceLink} target="_blank" removeUnderline>
            <InlineStack gap="0">
              {p.invoiceNumber || "-"}
              <Box>
                <Icon source={ExternalSmallIcon} />
              </Box>
            </InlineStack>
          </Link>
        ) : (
          "-"
        )}
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  // Event filter as ChoiceList
  const eventChoices = myEvents.map((ev) => ({
    label: ev.publicTitle,
    value: ev.id,
  }));
  const filters = [
    {
      key: "event",
      label: t("eventTransactions.event"),
      filter: (
        <ChoiceList
          title={t("eventTransactions.event")}
          titleHidden
          choices={eventChoices}
          selected={selectedEventId ? [selectedEventId] : []}
          onChange={(val) => setSelectedEventId(val[0] || "")}
          allowMultiple={false}
        />
      ),
      pinned: true,
    },
  ];

  // Applied filters
  const appliedFilters = [];
  if (selectedEventId) {
    appliedFilters.push({
      key: "event",
      label: `${t("eventTransactions.event")}: ${
        myEvents.find((ev) => ev.id === selectedEventId)?.publicTitle || ""
      }`,
      onRemove: () => setSelectedEventId(""),
    });
  }

  const isInitialLoading = isLoading && payments.length === 0;

  if (isInitialLoading) {
    return (
      <AppPageWrapper>
        <Page
          fullWidth
          title={t("eventTransactions.title")}
          subtitle={t("eventTransactions.subtitle")}
        >
          <TabsTableSkeleton />
        </Page>
      </AppPageWrapper>
    );
  }

  return (
    <AppPageWrapper>
      <Page
        fullWidth
        title={t("eventTransactions.title")}
        subtitle={t("eventTransactions.subtitle")}
      >
        <Card padding="0">
          <BlockStack gap="0">
            <IndexFilters
              tabs={tabs}
              selected={selectedTab}
              onSelect={setSelectedTab}
              queryValue={queryValue}
              queryPlaceholder={t("eventTransactions.searchPlaceholder")}
              onQueryChange={setQueryValue}
              onQueryClear={() => setQueryValue("")}
              filters={filters}
              cancelAction={{
                onAction: () => {
                  setSelectedTab(0);
                  setSelectedEventId("");
                  setQueryValue("");
                },
                disabled: false,
                loading: false,
              }}
              appliedFilters={appliedFilters}
              onClearAll={() => {
                setSelectedTab(0);
                setSelectedEventId("");
                setQueryValue("");
              }}
              mode={mode}
              setMode={setMode}
              loading={isFetching && payments.length > 0}
            />
            <IndexTable
              resourceName={{
                singular: t("eventTransactions.payment"),
                plural: t("eventTransactions.payments"),
              }}
              itemCount={payments.length}
              selectable={false}
              headings={headings as NonEmptyArray<IndexTableHeading>}
            >
              {isLoading
                ? [...Array(5)].map((_, i) => (
                    <IndexTable.Row id={`skeleton-${i}`} key={i} position={i}>
                      {headings.map((_, j) => (
                        <IndexTable.Cell key={j}>
                          <SkeletonBodyText lines={1} />
                        </IndexTable.Cell>
                      ))}
                    </IndexTable.Row>
                  ))
                : rowMarkup}
            </IndexTable>
          </BlockStack>
        </Card>
        {assignModalOpen && (
          <AssignTicketsModal
            open={assignModalOpen}
            onClose={handleAssignClose}
            payment={assignPayment}
            onSuccess={handleAssignSuccess}
          />
        )}
        {toast && (
          <Toast
            content={toast.content}
            error={toast.error}
            onDismiss={() => setToast(null)}
          />
        )}
      </Page>
    </AppPageWrapper>
  );
}
